import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import {
  EQUIPMENT_CATEGORY_META,
  EQUIPMENT_FILES_BUCKET,
  type EquipmentCategory,
  type EquipmentFileKind,
  type MaintenanceIntervalUnit,
  type MaintenanceKind,
} from './model';
import {
  buildDepreciationSchedule,
  depreciationAsOf,
  type DepreciationSchedule,
} from './depreciation';
import { todayIsoDate } from '@/lib/formatters';

// ---------------------------------------------------------------------------
// Types — columns from migrations 11 + 134.
// ---------------------------------------------------------------------------

export interface EquipmentSpec {
  label: string;
  value: string;
}

export interface Equipment {
  id: string;
  organization_id: string;
  asset_tag: string;
  name: string;
  category: string;
  equipment_type: string | null;
  description: string | null;
  manufacturer: string | null;
  model: string | null;
  serial_number: string | null;
  location_id: string | null;
  location_detail: string | null;
  assigned_to: string | null;
  status: string;
  condition: string | null;
  /** Purchase date. */
  acquired_at: string | null;
  in_service_on: string | null;
  purchased_from: string | null;
  purchase_price_cents: number | null;
  purchase_order_number: string | null;
  invoice_number: string | null;
  warranty_provider: string | null;
  warranty_expires_on: string | null;
  warranty_terms: string | null;
  depreciation_method: string;
  useful_life_years: number | null;
  salvage_value_cents: number | null;
  disposed_on: string | null;
  disposal_method: string | null;
  disposal_proceeds_cents: number | null;
  disposal_notes: string | null;
  specs: EquipmentSpec[];
  safety_notes: string | null;
  notes: string | null;
  /** Cover photo — storage key of one of this asset's photos. */
  image_path: string | null;
  image_uploaded_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  location: { id: string; name: string; code: string } | null;
  assignee: { id: string; full_name: string | null; email: string } | null;
}

export interface EquipmentListRow extends Equipment {
  /** Earliest next-due date across active maintenance schedules. */
  next_maintenance_on: string | null;
  active_plan_count: number;
  book_value_cents: number | null;
}

export type EquipmentPatch = Partial<
  Omit<
    Equipment,
    'id' | 'organization_id' | 'created_at' | 'updated_at' | 'created_by' | 'location' | 'assignee'
  >
>;

export interface EquipmentFile {
  id: string;
  organization_id: string;
  equipment_id: string;
  kind: EquipmentFileKind;
  label: string;
  storage_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_by: string | null;
  created_at: string;
}

export interface MaintenancePlan {
  id: string;
  organization_id: string;
  equipment_id: string;
  title: string;
  instructions: string | null;
  interval_value: number;
  interval_unit: MaintenanceIntervalUnit;
  next_due_on: string | null;
  last_completed_on: string | null;
  responsible: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MaintenanceLog {
  id: string;
  organization_id: string;
  equipment_id: string;
  plan_id: string | null;
  performed_on: string;
  kind: MaintenanceKind;
  summary: string;
  details: string | null;
  performed_by: string | null;
  cost_cents: number | null;
  downtime_hours: number | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

const EQUIPMENT_SELECT =
  '*, location:locations(id, name, code), assignee:profiles!equipment_assigned_to_fkey(id, full_name, email)';

function normalizeEquipment<T extends Equipment>(row: T): T {
  return { ...row, specs: Array.isArray(row.specs) ? row.specs : [] };
}

/** Depreciation inputs straight off an equipment row. */
export function equipmentSchedule(e: Equipment): DepreciationSchedule {
  return buildDepreciationSchedule({
    costCents: e.purchase_price_cents,
    salvageCents: e.salvage_value_cents,
    method: e.depreciation_method,
    usefulLifeYears: e.useful_life_years,
    startOn: e.in_service_on ?? e.acquired_at,
    disposedOn: e.disposed_on,
  });
}

// ---------------------------------------------------------------------------
// Equipment master
// ---------------------------------------------------------------------------

export function useEquipmentList(params?: { category?: EquipmentCategory }) {
  return useQuery({
    queryKey: ['equipment', 'list', params?.category ?? 'all'],
    queryFn: async () => {
      let q = supabase
        .from('equipment' as never)
        .select(`${EQUIPMENT_SELECT}, equipment_maintenance_plans(next_due_on, is_active)`)
        .order('asset_tag');
      if (params?.category) q = q.eq('category', params.category);
      const { data, error } = await q;
      if (error) throw error;
      const today = todayIsoDate();
      return (
        data as unknown as Array<
          Equipment & {
            equipment_maintenance_plans?: Array<{ next_due_on: string | null; is_active: boolean }>;
          }
        >
      ).map((raw) => {
        const { equipment_maintenance_plans: plans, ...rest } = raw;
        const active = (plans ?? []).filter((p) => p.is_active);
        const next = active
          .map((p) => p.next_due_on)
          .filter((d): d is string => !!d)
          .sort()[0];
        const row = normalizeEquipment(rest as Equipment);
        return {
          ...row,
          next_maintenance_on: next ?? null,
          active_plan_count: active.length,
          book_value_cents: depreciationAsOf(equipmentSchedule(row), today)?.bookValueCents ?? null,
        } satisfies EquipmentListRow;
      });
    },
  });
}

export function useEquipment(id: string | undefined) {
  return useQuery({
    queryKey: ['equipment', 'detail', id],
    enabled: !!id,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('equipment' as never)
        .select(EQUIPMENT_SELECT)
        .eq('id', id!)
        .single();
      if (error) throw error;
      return normalizeEquipment(data as unknown as Equipment);
    },
  });
}

export interface CreateEquipmentInput {
  asset_tag: string;
  name: string;
  category: EquipmentCategory;
  equipment_type?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  serial_number?: string | null;
  status?: string;
  location_id?: string | null;
  acquired_at?: string | null;
  purchase_price_cents?: number | null;
}

export function useCreateEquipment() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateEquipmentInput) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const assetTag = input.asset_tag.trim();
      const name = input.name.trim();
      if (!assetTag) throw new Error('Asset tag is required.');
      if (!name) throw new Error('Name is required.');

      const payload = {
        organization_id: profile.organization_id,
        created_by: profile.id,
        asset_tag: assetTag,
        name,
        category: input.category,
        equipment_type: blankToNull(input.equipment_type),
        manufacturer: blankToNull(input.manufacturer),
        model: blankToNull(input.model),
        serial_number: blankToNull(input.serial_number),
        status: input.status ?? 'in_service',
        location_id: input.location_id ?? null,
        acquired_at: input.acquired_at || null,
        purchase_price_cents: input.purchase_price_cents ?? null,
        depreciation_method: 'straight_line',
        useful_life_years: EQUIPMENT_CATEGORY_META[input.category].defaultLifeYears,
      };

      const { data, error } = await supabase
        .from('equipment' as never)
        .insert(payload as never)
        .select(EQUIPMENT_SELECT)
        .single();
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          throw new Error(`Asset tag “${assetTag}” is already in use.`);
        }
        throw error;
      }
      return normalizeEquipment(data as unknown as Equipment);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['equipment'] });
    },
  });
}

export function useUpdateEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: EquipmentPatch }) => {
      const patch: Record<string, unknown> = { ...input.patch };
      for (const [key, value] of Object.entries(patch)) {
        if (typeof value === 'string') {
          const trimmed = value.trim();
          patch[key] = trimmed === '' && key !== 'asset_tag' && key !== 'name' ? null : trimmed;
        }
      }
      if (patch.asset_tag === '') throw new Error('Asset tag can’t be empty.');
      if (patch.name === '') throw new Error('Name can’t be empty.');

      const { data, error } = await supabase
        .from('equipment' as never)
        .update(patch as never)
        .eq('id', input.id)
        .select(EQUIPMENT_SELECT)
        .single();
      if (error) {
        if ((error as { code?: string }).code === '23505') {
          throw new Error(`Asset tag “${String(patch.asset_tag ?? '')}” is already in use.`);
        }
        throw error;
      }
      return normalizeEquipment(data as unknown as Equipment);
    },
    onSuccess: (row) => {
      qc.setQueryData(['equipment', 'detail', row.id], row);
      qc.invalidateQueries({ queryKey: ['equipment', 'list'] });
    },
  });
}

/**
 * Delete equipment rows. Child rows cascade in the database; the stored
 * files are removed afterwards, best-effort, so a storage hiccup never
 * blocks the delete itself.
 */
export function useDeleteEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[] }) => {
      if (input.ids.length === 0) return;
      const { data: files } = await supabase
        .from('equipment_files' as never)
        .select('storage_path')
        .in('equipment_id', input.ids);
      const paths = ((files ?? []) as unknown as Array<{ storage_path: string }>).map(
        (f) => f.storage_path,
      );

      const { error } = await supabase
        .from('equipment' as never)
        .delete()
        .in('id', input.ids);
      if (error) throw error;

      if (paths.length > 0) {
        await supabase.storage.from(EQUIPMENT_FILES_BUCKET).remove(paths);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['equipment'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Files — photos and documents in the private `equipment-files` bucket,
// keyed `<organization_id>/<equipment_id>/<stamp>-<filename>`.
// ---------------------------------------------------------------------------

export function useEquipmentFiles(equipmentId: string | undefined) {
  return useQuery({
    queryKey: ['equipment-files', equipmentId],
    enabled: !!equipmentId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('equipment_files' as never)
        .select('*')
        .eq('equipment_id', equipmentId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as unknown as EquipmentFile[];
    },
  });
}

function buildEquipmentFilePath(orgId: string, equipmentId: string, filename: string): string {
  const cleaned = filename.replace(/[^a-zA-Z0-9._-]+/g, '_');
  return `${orgId}/${equipmentId}/${Date.now()}-${cleaned}`;
}

/**
 * Upload one file and register it. Pass `makeCover` to also point the
 * equipment's cover photo at it. Removes the stored object again if the
 * metadata insert fails.
 */
export function useUploadEquipmentFile() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      equipmentId: string;
      file: File;
      kind: EquipmentFileKind;
      label?: string | null;
      makeCover?: boolean;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const path = buildEquipmentFilePath(profile.organization_id, input.equipmentId, input.file.name);

      const { error: uploadErr } = await supabase.storage
        .from(EQUIPMENT_FILES_BUCKET)
        .upload(path, input.file, {
          upsert: false,
          cacheControl: '3600',
          contentType: input.file.type || undefined,
        });
      if (uploadErr) throw uploadErr;

      const { data, error: dbErr } = await supabase
        .from('equipment_files' as never)
        .insert({
          organization_id: profile.organization_id,
          equipment_id: input.equipmentId,
          kind: input.kind,
          label: input.label?.trim() || input.file.name,
          storage_path: path,
          mime_type: input.file.type || null,
          size_bytes: input.file.size,
          uploaded_by: profile.id,
        } as never)
        .select('*')
        .single();
      if (dbErr) {
        await supabase.storage.from(EQUIPMENT_FILES_BUCKET).remove([path]);
        throw dbErr;
      }

      if (input.makeCover) {
        const { error: coverErr } = await supabase
          .from('equipment' as never)
          .update({ image_path: path, image_uploaded_at: new Date().toISOString() } as never)
          .eq('id', input.equipmentId);
        if (coverErr) throw coverErr;
      }
      return data as unknown as EquipmentFile;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['equipment-files', vars.equipmentId] });
      if (vars.makeCover) qc.invalidateQueries({ queryKey: ['equipment'] });
    },
  });
}

export function useRenameEquipmentFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; equipmentId: string; label: string }) => {
      const label = input.label.trim();
      if (!label) throw new Error('Name can’t be empty.');
      const { error } = await supabase
        .from('equipment_files' as never)
        .update({ label } as never)
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['equipment-files', vars.equipmentId] });
    },
  });
}

/**
 * Delete a file. When it was the cover photo, the newest remaining photo
 * takes over (or the cover is cleared).
 */
export function useDeleteEquipmentFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { file: EquipmentFile; coverPath: string | null }) => {
      const { file } = input;
      const { error } = await supabase
        .from('equipment_files' as never)
        .delete()
        .eq('id', file.id);
      if (error) throw error;
      await supabase.storage.from(EQUIPMENT_FILES_BUCKET).remove([file.storage_path]);

      if (input.coverPath && input.coverPath === file.storage_path) {
        const { data: next } = await supabase
          .from('equipment_files' as never)
          .select('storage_path')
          .eq('equipment_id', file.equipment_id)
          .eq('kind', 'photo')
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        const nextPath = (next as unknown as { storage_path: string } | null)?.storage_path ?? null;
        const { error: coverErr } = await supabase
          .from('equipment' as never)
          .update({
            image_path: nextPath,
            image_uploaded_at: nextPath ? new Date().toISOString() : null,
          } as never)
          .eq('id', file.equipment_id);
        if (coverErr) throw coverErr;
      }
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['equipment-files', vars.file.equipment_id] });
      qc.invalidateQueries({ queryKey: ['equipment'] });
    },
  });
}

export function useSetEquipmentCover() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { equipmentId: string; path: string }) => {
      const { error } = await supabase
        .from('equipment' as never)
        .update({ image_path: input.path, image_uploaded_at: new Date().toISOString() } as never)
        .eq('id', input.equipmentId);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['equipment'] });
    },
  });
}

/** Five-minute signed URL; the bucket is private. */
export function useEquipmentFileUrl(path: string | null | undefined) {
  return useQuery({
    queryKey: ['equipment-file-url', path],
    enabled: !!path,
    staleTime: 4 * 60 * 1000,
    queryFn: async () => {
      if (!path) return null;
      const { data, error } = await supabase.storage
        .from(EQUIPMENT_FILES_BUCKET)
        .createSignedUrl(path, 60 * 5);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
  });
}

// ---------------------------------------------------------------------------
// Maintenance schedules + log
// ---------------------------------------------------------------------------

export function useMaintenancePlans(equipmentId: string | undefined) {
  return useQuery({
    queryKey: ['equipment-maintenance-plans', equipmentId],
    enabled: !!equipmentId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('equipment_maintenance_plans' as never)
        .select('*')
        .eq('equipment_id', equipmentId!)
        .order('is_active', { ascending: false })
        .order('next_due_on', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return data as unknown as MaintenancePlan[];
    },
  });
}

export interface MaintenancePlanValues {
  title: string;
  instructions: string | null;
  interval_value: number;
  interval_unit: MaintenanceIntervalUnit;
  last_completed_on: string | null;
  next_due_on: string | null;
  responsible: string | null;
  is_active: boolean;
}

export function useSaveMaintenancePlan() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: {
      id?: string;
      equipmentId: string;
      values: MaintenancePlanValues;
    }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const values = {
        ...input.values,
        title: input.values.title.trim(),
        instructions: blankToNull(input.values.instructions),
        responsible: blankToNull(input.values.responsible),
      };
      if (!values.title) throw new Error('Give the schedule a name.');

      const query = input.id
        ? supabase
            .from('equipment_maintenance_plans' as never)
            .update(values as never)
            .eq('id', input.id)
        : supabase.from('equipment_maintenance_plans' as never).insert({
            ...values,
            organization_id: profile.organization_id,
            equipment_id: input.equipmentId,
            created_by: profile.id,
          } as never);
      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['equipment-maintenance-plans', vars.equipmentId] });
      qc.invalidateQueries({ queryKey: ['equipment', 'list'] });
    },
  });
}

export function useDeleteMaintenancePlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; equipmentId: string }) => {
      const { error } = await supabase
        .from('equipment_maintenance_plans' as never)
        .delete()
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['equipment-maintenance-plans', vars.equipmentId] });
      qc.invalidateQueries({ queryKey: ['equipment-maintenance-logs', vars.equipmentId] });
      qc.invalidateQueries({ queryKey: ['equipment', 'list'] });
    },
  });
}

export function useMaintenanceLogs(equipmentId: string | undefined) {
  return useQuery({
    queryKey: ['equipment-maintenance-logs', equipmentId],
    enabled: !!equipmentId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('equipment_maintenance_logs' as never)
        .select('*')
        .eq('equipment_id', equipmentId!)
        .order('performed_on', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data as unknown as MaintenanceLog[]).map((row) => ({
        ...row,
        downtime_hours: row.downtime_hours == null ? null : Number(row.downtime_hours),
      }));
    },
  });
}

export interface MaintenanceLogValues {
  plan_id: string | null;
  performed_on: string;
  kind: MaintenanceKind;
  summary: string;
  details: string | null;
  performed_by: string | null;
  cost_cents: number | null;
  downtime_hours: number | null;
}

export function useSaveMaintenanceLog() {
  const qc = useQueryClient();
  const { profile } = useAuth();
  return useMutation({
    mutationFn: async (input: { id?: string; equipmentId: string; values: MaintenanceLogValues }) => {
      if (!profile?.organization_id) throw new Error('Not signed in.');
      const values = {
        ...input.values,
        summary: input.values.summary.trim(),
        details: blankToNull(input.values.details),
        performed_by: blankToNull(input.values.performed_by),
      };
      if (!values.summary) throw new Error('Describe the work that was done.');
      if (!values.performed_on) throw new Error('Pick the date the work was done.');

      const query = input.id
        ? supabase
            .from('equipment_maintenance_logs' as never)
            .update(values as never)
            .eq('id', input.id)
        : supabase.from('equipment_maintenance_logs' as never).insert({
            ...values,
            organization_id: profile.organization_id,
            equipment_id: input.equipmentId,
            created_by: profile.id,
          } as never);
      const { error } = await query;
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['equipment-maintenance-logs', vars.equipmentId] });
      qc.invalidateQueries({ queryKey: ['equipment-maintenance-plans', vars.equipmentId] });
      qc.invalidateQueries({ queryKey: ['equipment', 'list'] });
    },
  });
}

export function useDeleteMaintenanceLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; equipmentId: string }) => {
      const { error } = await supabase
        .from('equipment_maintenance_logs' as never)
        .delete()
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['equipment-maintenance-logs', vars.equipmentId] });
    },
  });
}

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}
